import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Alert, Accordion } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { nightModeApi, extensionsApi, timeConditionsApi, recordingsApi, inboundRoutesApi } from '../services/api'
import { DestinationSelector } from '../components/common'
import { useConfirm } from '../components/common/ConfirmDialog'

interface NightModeEntry {
  id: string
  name: string
  description: string
  feature_code: string
  is_enabled: boolean
  live_enabled: boolean | null
  effectively_enabled?: boolean
  gated_by_tc?: boolean
  active_tc_matches_now?: boolean | null
  destination_type: string
  destination_id: string
  destination_name: string
  announcement_id: string
  announcement_name: string
  extension_scope: string[] | null
  inbound_route_scope: string[] | null
  auto_enable_time_condition_id: string
  auto_disable_tc_ids: string[] | null
  active_time_condition_id: string
  linked_nm_ids: string[] | null
  time_condition_name: string
  active_time_condition_name: string
  enabled_at: string
  enabled_by_username: string
  affected_extensions_count: number
  affected_inbound_routes_count: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface SelectOption {
  id: string
  name: string
  extension?: string
  did?: string
  is_active?: boolean
}

const destinationTypes = [
  { value: 'extension', label: 'Nebenstelle', icon: 'bi-telephone' },
  { value: 'ring_group', label: 'Rufgruppe', icon: 'bi-people' },
  { value: 'queue', label: 'Warteschlange', icon: 'bi-list-ol' },
  { value: 'ivr', label: 'IVR Menü', icon: 'bi-menu-button-wide' },
  { value: 'time_condition', label: 'Zeitbedingung', icon: 'bi-clock' },
  { value: 'conference', label: 'Konferenz', icon: 'bi-people-fill' },
  { value: 'voicemail', label: 'Voicemail', icon: 'bi-voicemail' },
  { value: 'parking', label: 'Parkplatz', icon: 'bi-p-square' },
  { value: 'night_mode', label: 'Nachtschaltung', icon: 'bi-moon-stars' },
  { value: 'announcement', label: 'Ansage', icon: 'bi-megaphone' },
  { value: 'external_dest', label: 'Externes Ziel', icon: 'bi-box-arrow-up-right' },
  { value: 'external', label: 'Externe Nummer', icon: 'bi-telephone-outbound' },
  { value: 'hangup', label: 'Auflegen', icon: 'bi-telephone-x' },
]

const emptyForm = {
  name: '',
  description: '',
  feature_code: '',
  destination_type: '',
  destination_id: '',
  announcement_id: '',
  extension_scope: null as string[] | null,
  inbound_route_scope: null as string[] | null,
  auto_enable_time_condition_id: '',
  auto_disable_tc_ids: [] as string[],
  active_time_condition_id: '',
  linked_nm_ids: [] as string[],
  is_active: true
}

// Allowed destination types for night mode
const NM_ALLOWED_TYPES = [
  'extension',
  'ring_group',
  'queue',
  'ivr',
  'time_condition',
  'conference',
  'voicemail',
  'parking',
  'night_mode',
  'announcement',
  'external_dest',
  'external',
  'hangup',
]

export default function NightMode() {
  const confirm = useConfirm()
  const [entries, setEntries] = useState<NightModeEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingEntry, setEditingEntry] = useState<NightModeEntry | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [toggling, setToggling] = useState<string | null>(null)
  const [cloneSource, setCloneSource] = useState<NightModeEntry | null>(null)
  const [cloneName, setCloneName] = useState('')
  const [cloneFeatureCode, setCloneFeatureCode] = useState('')

  // Options for other dropdowns
  const [extensions, setExtensions] = useState<SelectOption[]>([])
  const [inboundRoutes, setInboundRoutes] = useState<SelectOption[]>([])
  const [timeConditions, setTimeConditions] = useState<SelectOption[]>([])
  const [recordings, setRecordings] = useState<SelectOption[]>([])
  const [allExtensions, setAllExtensions] = useState(true)
  const [scopeType, setScopeType] = useState<'extensions' | 'inbound_routes'>('extensions')

  useEffect(() => {
    loadAll()
  }, [])

  const loadAll = async () => {
    try {
      const [nmRes, extRes, irRes, tcRes, recRes] = await Promise.all([
        nightModeApi.list(),
        extensionsApi.list({ limit: 1000 }),
        inboundRoutesApi.list({ limit: 1000 }),
        timeConditionsApi.list({ limit: 1000 }),
        recordingsApi.list({ limit: 1000 }),
      ])
      setEntries(nmRes.data.items || [])
      setExtensions((extRes.data.items || []).map((e: { id: string; extension: string; name: string }) => ({
        id: e.id,
        name: `${e.extension} - ${e.name}`,
        extension: e.extension
      })))
      setInboundRoutes((irRes.data.items || []).map((r: { id: string; name: string; did_pattern: string }) => ({
        id: r.id,
        name: r.did_pattern ? `${r.did_pattern} - ${r.name}` : r.name,
        did: r.did_pattern
      })))
      setTimeConditions((tcRes.data.items || []).map((t: { id: string; name: string; is_active?: boolean }) => ({ id: t.id, name: t.name, is_active: t.is_active })))
      setRecordings((recRes.data.items || []).map((r: { id: string; name: string }) => ({ id: r.id, name: r.name })))
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingEntry(null)
    setFormData(emptyForm)
    setAllExtensions(true)
    setScopeType('extensions')
    setShowModal(true)
  }

  const openEditModal = (entry: NightModeEntry) => {
    setEditingEntry(entry)
    setFormData({
      name: entry.name,
      description: entry.description || '',
      feature_code: entry.feature_code || '*28',
      destination_type: entry.destination_type || '',
      destination_id: entry.destination_id || '',
      announcement_id: entry.announcement_id || '',
      extension_scope: entry.extension_scope,
      inbound_route_scope: entry.inbound_route_scope,
      auto_enable_time_condition_id: entry.auto_enable_time_condition_id || '',
      auto_disable_tc_ids: entry.auto_disable_tc_ids || [],
      active_time_condition_id: entry.active_time_condition_id || '',
      linked_nm_ids: entry.linked_nm_ids || [],
      is_active: entry.is_active
    })
    // Determine scope type from existing data
    if (entry.inbound_route_scope && entry.inbound_route_scope.length > 0) {
      setScopeType('inbound_routes')
    } else {
      setScopeType('extensions')
    }
    setAllExtensions(entry.extension_scope === null)
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Clear the scope that is not selected
      const data = {
        ...formData,
        extension_scope: scopeType === 'extensions' ? (allExtensions ? null : formData.extension_scope) : null,
        inbound_route_scope: scopeType === 'inbound_routes' && formData.inbound_route_scope?.length ? formData.inbound_route_scope : null,
        announcement_id: formData.announcement_id || null,
        auto_enable_time_condition_id: formData.auto_enable_time_condition_id || null,
        auto_disable_tc_ids: formData.auto_disable_tc_ids?.length ? formData.auto_disable_tc_ids : null,
        active_time_condition_id: formData.active_time_condition_id || null,
        linked_nm_ids: formData.linked_nm_ids?.length ? formData.linked_nm_ids : null,
        destination_id: formData.destination_id || null,
      }

      if (editingEntry) {
        await nightModeApi.update(editingEntry.id, data)
        toast.success('Nachtschaltung aktualisiert')
      } else {
        await nightModeApi.create(data)
        toast.success('Nachtschaltung erstellt')
      }
      setShowModal(false)
      loadAll()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const openCloneModal = (entry: NightModeEntry) => {
    setCloneSource(entry)
    setCloneName(`${entry.name} (Kopie)`)
    setCloneFeatureCode('')
  }

  const closeCloneModal = () => {
    setCloneSource(null)
    setCloneName('')
    setCloneFeatureCode('')
  }

  const handleClone = async () => {
    if (!cloneSource) return
    if (!cloneName.trim()) {
      toast.error('Name ist erforderlich')
      return
    }
    try {
      await nightModeApi.clone(cloneSource.id, {
        new_name: cloneName.trim(),
        new_feature_code: cloneFeatureCode.trim() || undefined,
      })
      toast.success('Nachtschaltung geklont')
      closeCloneModal()
      loadAll()
    } catch {
      toast.error('Fehler beim Klonen')
    }
  }

  const handleDelete = async (entry: NightModeEntry) => {
    const confirmed = await confirm({
      title: 'Nachtschaltung löschen',
      message: `Nachtschaltung "${entry.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await nightModeApi.delete(entry.id)
      toast.success('Nachtschaltung gelöscht')
      loadAll()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const handleToggle = async (entry: NightModeEntry) => {
    // Use live state as truth - toggle based on what Asterisk actually has
    const currentState = entry.live_enabled !== null ? entry.live_enabled : entry.is_enabled
    setToggling(entry.id)
    try {
      const newState = !currentState
      await nightModeApi.toggle(entry.id, newState)
      toast.success(newState
        ? `Nachtschaltung "${entry.name}" aktiviert`
        : `Nachtschaltung "${entry.name}" deaktiviert`
      )
      loadAll()
    } catch {
      toast.error('Fehler beim Umschalten')
    } finally {
      setToggling(null)
    }
  }

  const handleSyncFromPhone = async (entry: NightModeEntry) => {
    setToggling(entry.id)
    try {
      await nightModeApi.syncFromPhone(entry.id)
      toast.success(`"${entry.name}" von Asterisk synchronisiert`)
      loadAll()
    } catch {
      toast.error('Fehler beim Synchronisieren')
    } finally {
      setToggling(null)
    }
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('de-DE')
  }

  const activeNightModes = entries.filter(e => e.live_enabled !== null ? e.live_enabled : e.is_enabled)

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4><i className="bi bi-moon-stars me-2"></i>Nachtschaltung</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Nachtschaltung
        </Button>
      </div>

      {activeNightModes.length > 0 && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading className="h6">
            <i className="bi bi-moon-fill me-2"></i>
            Aktive Nachtschaltungen
          </Alert.Heading>
          <div className="d-flex flex-wrap gap-2 mt-2">
            {activeNightModes.map(nm => (
              <Badge key={nm.id} bg="warning" className="fs-6 p-2">
                {nm.name} - {nm.inbound_route_scope && nm.inbound_route_scope.length > 0
                  ? `${nm.affected_inbound_routes_count} Routen betroffen`
                  : nm.extension_scope
                    ? `${nm.affected_extensions_count} Nebenstellen betroffen`
                    : 'Alle Nebenstellen betroffen'}
              </Badge>
            ))}
          </div>
        </Alert>
      )}

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th style={{width: '50px'}}>Status</th>
              <th>Name</th>
              <th>Ziel</th>
              <th>Betroffen</th>
              <th>Feature-Code</th>
              <th>Auto-Aktivierung</th>
              <th>Aktiviert</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-4">Laden...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-4 text-muted">Keine Nachtschaltungen konfiguriert</td></tr>
            ) : (
              entries.map(e => {
                const liveOn = e.live_enabled !== null ? e.live_enabled : (e.effectively_enabled ?? e.is_enabled)
                const outOfSync = e.live_enabled !== null && e.live_enabled !== e.is_enabled
                // User wants on (is_enabled) but TC gate is closed → "wartet"
                const gated = !!e.gated_by_tc
                return (
                <tr key={e.id} className={liveOn ? 'table-warning' : gated ? 'table-info' : ''}>
                  <td className="text-center">
                    <Button
                      variant={liveOn ? 'warning' : gated ? 'info' : 'outline-secondary'}
                      size="sm"
                      onClick={() => handleToggle(e)}
                      disabled={toggling === e.id}
                      title={liveOn ? 'Deaktivieren' : gated ? 'Eingeschaltet, aber durch Zeitbedingung blockiert' : 'Aktivieren'}
                    >
                      {toggling === e.id ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <i className={`bi ${liveOn ? 'bi-moon-fill' : gated ? 'bi-moon-stars' : 'bi-moon'}`}></i>
                      )}
                    </Button>
                    {outOfSync && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-1"
                        onClick={() => handleSyncFromPhone(e)}
                        disabled={toggling === e.id}
                        title="Status von Asterisk abweichend - klicken zum Synchronisieren"
                      >
                        <i className="bi bi-exclamation-triangle text-warning"></i>
                      </Button>
                    )}
                  </td>
                  <td>
                    <strong>{e.name}</strong>
                    {e.description && <div className="small text-muted">{e.description}</div>}
                  </td>
                  <td>
                    {(() => {
                      const dt = destinationTypes.find(d => d.value === e.destination_type?.toLowerCase())
                      return dt ? (
                        <Badge bg="info">
                          <i className={`bi ${dt.icon} me-1`}></i>
                          {dt.label}
                        </Badge>
                      ) : e.destination_type ? (
                        <Badge bg="info">{e.destination_type}</Badge>
                      ) : null
                    })()}
                    {e.destination_name && (
                      <div className="small">{e.destination_name}</div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      {e.inbound_route_scope && e.inbound_route_scope.length > 0 ? (
                        <Badge bg="info">
                          <i className="bi bi-telephone-inbound me-1"></i>
                          {e.inbound_route_scope.length} Eingehende Routen
                        </Badge>
                      ) : (
                        <Badge bg={e.extension_scope ? 'secondary' : 'primary'}>
                          <i className="bi bi-telephone me-1"></i>
                          {e.extension_scope ? `${e.extension_scope.length} Nebenstellen` : 'Alle Nebenstellen'}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td>
                    {e.feature_code ? (
                      <code>{e.feature_code}</code>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      {e.active_time_condition_id && (
                        <Badge bg={e.active_tc_matches_now ? 'success' : 'secondary'} title="Nur aktiv während Zeitbedingung">
                          <i className="bi bi-clock-history me-1"></i>
                          {e.active_tc_matches_now ? 'Im Zeitfenster' : 'Außerhalb Zeitfenster'}
                        </Badge>
                      )}
                      {gated && (
                        <Badge bg="info" title="Eingeschaltet aber durch Zeitbedingung blockiert">
                          <i className="bi bi-pause-circle me-1"></i>Wartet auf Zeitbedingung
                        </Badge>
                      )}
                      {e.time_condition_name && (
                        <Badge bg="secondary" title="Auto-Aktivierung">
                          <i className="bi bi-clock me-1"></i>{e.time_condition_name}
                        </Badge>
                      )}
                      {!e.time_condition_name && !e.active_time_condition_id && !gated && (
                        <span className="text-muted">Manuell</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {liveOn && e.enabled_at ? (
                      <div>
                        <div className="small">{formatDate(e.enabled_at)}</div>
                        {e.enabled_by_username ? (
                          <div className="small text-muted">von {e.enabled_by_username}</div>
                        ) : outOfSync && (
                          <div className="small text-muted"><i className="bi bi-telephone-fill me-1" style={{fontSize: '0.65rem'}}></i>via Telefon</div>
                        )}
                      </div>
                    ) : liveOn && !e.enabled_at ? (
                      <div className="small text-muted"><i className="bi bi-telephone-fill me-1"></i>via Telefon</div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(e)} title="Bearbeiten">
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openCloneModal(e)} title="Klonen">
                      <i className="bi bi-copy"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(e)} title="Löschen">
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </div>

      <Card className="mt-4">
        <Card.Header>
          <i className="bi bi-info-circle me-2"></i>Hinweise zur Nachtschaltung
        </Card.Header>
        <Card.Body>
          <ul className="mb-0">
            <li>Wenn aktiviert, werden alle (oder ausgewählte) Nebenstellen zum konfigurierten Ziel umgeleitet.</li>
            <li>Der Feature-Code kann auf Telefonen als BLF-Taste konfiguriert werden, um die Nachtschaltung zu aktivieren/deaktivieren.</li>
            <li>Optional kann eine Zeitbedingung für die automatische Aktivierung verknüpft werden.</li>
            <li>Eine Ansage kann vor der Weiterleitung abgespielt werden.</li>
          </ul>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-moon-stars me-2"></i>
            {editingEntry ? 'Nachtschaltung bearbeiten' : 'Neue Nachtschaltung'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Nachtschaltung Hauptnummer"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Feature-Code</Form.Label>
                  <Form.Control
                    value={formData.feature_code}
                    onChange={(e) => setFormData({...formData, feature_code: e.target.value})}
                    placeholder="wird automatisch vergeben"
                  />
                  <Form.Text className="text-muted">
                    {editingEntry ? 'Zum Aktivieren/Deaktivieren per Telefon' : 'Leer lassen für automatische Vergabe'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optionale Beschreibung..."
              />
            </Form.Group>

            <Card className="mb-3">
              <Card.Header>
                <i className="bi bi-signpost-2 me-2"></i>Ziel bei aktivierter Nachtschaltung
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Ziel *</Form.Label>
                  <DestinationSelector
                    destinationType={formData.destination_type}
                    destinationValue={formData.destination_id}
                    onChange={(type, value) => setFormData({
                      ...formData,
                      destination_type: type,
                      destination_id: value
                    })}
                    allowedTypes={NM_ALLOWED_TYPES}
                    excludeType="night_mode"
                    excludeId={editingEntry?.id}
                    allowExternal={true}
                    placeholder="Ziel suchen..."
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label>Ansage vor Weiterleitung</Form.Label>
                  <Form.Select
                    value={formData.announcement_id}
                    onChange={(e) => setFormData({...formData, announcement_id: e.target.value})}
                  >
                    <option value="">-- Keine Ansage --</option>
                    {recordings.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">z.B. "Wir sind derzeit nicht erreichbar..."</Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>
                <i className="bi bi-diagram-2 me-2"></i>Betroffene Objekte
              </Card.Header>
              <Card.Body>
                {/* Scope Type Selection */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Was soll umgeleitet werden?</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="scope-type-extensions"
                      name="scopeType"
                      label={
                        <span>
                          <i className="bi bi-telephone me-1"></i>
                          Nebenstellen
                          <div className="small text-muted">Anrufe an Nebenstellen (intern + extern)</div>
                        </span>
                      }
                      checked={scopeType === 'extensions'}
                      onChange={() => {
                        setScopeType('extensions')
                        setFormData({...formData, inbound_route_scope: null})
                      }}
                    />
                    <Form.Check
                      type="radio"
                      id="scope-type-inbound"
                      name="scopeType"
                      label={
                        <span>
                          <i className="bi bi-telephone-inbound me-1"></i>
                          Eingehende Routen
                          <div className="small text-muted">Nur externe Anrufe über bestimmte DIDs</div>
                        </span>
                      }
                      checked={scopeType === 'inbound_routes'}
                      onChange={() => {
                        setScopeType('inbound_routes')
                        setFormData({...formData, extension_scope: null})
                        setAllExtensions(true)
                      }}
                    />
                  </div>
                </Form.Group>

                <hr />

                {/* Extensions Section */}
                {scopeType === 'extensions' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="radio"
                        id="scope-all"
                        name="scope"
                        label="Alle Nebenstellen"
                        checked={allExtensions}
                        onChange={() => {
                          setAllExtensions(true)
                          setFormData({...formData, extension_scope: null})
                        }}
                      />
                      <Form.Check
                        type="radio"
                        id="scope-selected"
                        name="scope"
                        label="Nur ausgewählte Nebenstellen"
                        checked={!allExtensions}
                        onChange={() => {
                          setAllExtensions(false)
                          setFormData({...formData, extension_scope: []})
                        }}
                      />
                    </Form.Group>

                    {!allExtensions && (
                      <Form.Group>
                        <Form.Label>Nebenstellen auswählen</Form.Label>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '0.5rem' }}>
                          {extensions.map(ext => (
                            <Form.Check
                              key={ext.id}
                              type="checkbox"
                              id={`ext-${ext.id}`}
                              label={ext.name}
                              checked={formData.extension_scope?.includes(ext.id) || false}
                              onChange={(e) => {
                                const newScope = formData.extension_scope ? [...formData.extension_scope] : []
                                if (e.target.checked) {
                                  newScope.push(ext.id)
                                } else {
                                  const idx = newScope.indexOf(ext.id)
                                  if (idx > -1) newScope.splice(idx, 1)
                                }
                                setFormData({...formData, extension_scope: newScope})
                              }}
                            />
                          ))}
                        </div>
                        <Form.Text className="text-muted">
                          {formData.extension_scope?.length || 0} von {extensions.length} Nebenstellen ausgewählt
                        </Form.Text>
                      </Form.Group>
                    )}
                  </>
                )}

                {/* Inbound Routes Section */}
                {scopeType === 'inbound_routes' && (
                  <>
                    {inboundRoutes.length === 0 ? (
                      <Alert variant="warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Keine eingehenden Routen konfiguriert. Bitte erstellen Sie zuerst eingehende Routen.
                      </Alert>
                    ) : (
                      <Form.Group>
                        <Form.Label>Eingehende Routen auswählen</Form.Label>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '0.5rem' }}>
                          {inboundRoutes.map(route => (
                            <Form.Check
                              key={route.id}
                              type="checkbox"
                              id={`route-${route.id}`}
                              label={route.name}
                              checked={formData.inbound_route_scope?.includes(route.id) || false}
                              onChange={(e) => {
                                const newScope = formData.inbound_route_scope ? [...formData.inbound_route_scope] : []
                                if (e.target.checked) {
                                  newScope.push(route.id)
                                } else {
                                  const idx = newScope.indexOf(route.id)
                                  if (idx > -1) newScope.splice(idx, 1)
                                }
                                setFormData({...formData, inbound_route_scope: newScope})
                              }}
                            />
                          ))}
                        </div>
                        <Form.Text className="text-muted">
                          {formData.inbound_route_scope?.length || 0} von {inboundRoutes.length} Routen ausgewählt
                        </Form.Text>
                      </Form.Group>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

            <Accordion className="mb-3" alwaysOpen>
              <Accordion.Item eventKey="auto">
                <Accordion.Header>
                  <i className="bi bi-clock me-2"></i>Automatische Aktivierung / Deaktivierung (optional)
                </Accordion.Header>
                <Accordion.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Nur aktiv während Zeitbedingung</Form.Label>
                    <Form.Select
                      value={formData.active_time_condition_id}
                      onChange={(e) => setFormData({...formData, active_time_condition_id: e.target.value})}
                    >
                      <option value="">-- Keine (manuell steuerbar) --</option>
                      {timeConditions
                        .filter(tc => tc.is_active === false ||
                                      tc.id === formData.active_time_condition_id)
                        .map(tc => (
                          <option key={tc.id} value={tc.id}>
                            {tc.name}{tc.is_active === false ? '' : ' (jetzt aktiv — wird ausgeblendet sobald gespeichert)'}
                          </option>
                        ))
                      }
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Wenn gesetzt, ist die Nachtschaltung <strong>nur</strong> während dieser Zeitbedingung aktiv — auch ein manuelles Einschalten greift ausserhalb der Zeit nicht.
                      Stärkste Bindung; überschreibt Auto-Aktivierung und Auto-Deaktivierung.
                      <br />
                      <i className="bi bi-info-circle me-1"></i>
                      <strong>Hinweis:</strong> Hier werden nur <strong>inaktive</strong> Zeitbedingungen angeboten.
                      Aktive TCs würden im Dialplan ihre eigene Match-Aktion ausführen — für die reine Zeit-Steuerung der Nachtschaltung verwende eine Zeitbedingung ohne Aktion (Status "Inaktiv").
                      Die Zeitfenster werden trotzdem zur Steuerung herangezogen.
                    </Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Zeitbedingung für Auto-Aktivierung</Form.Label>
                    <Form.Select
                      value={formData.auto_enable_time_condition_id}
                      onChange={(e) => setFormData({...formData, auto_enable_time_condition_id: e.target.value})}
                    >
                      <option value="">-- Keine (nur manuell) --</option>
                      {timeConditions.map(tc => (
                        <option key={tc.id} value={tc.id}>{tc.name}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Wenn gewählt, wird die Nachtschaltung automatisch aktiviert, wenn die Zeitbedingung zutrifft.
                    </Form.Text>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Zeitbedingungen mit Deaktivierungs-Recht</Form.Label>
                    <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px' }}>
                      {timeConditions.length === 0
                        ? <span className="text-muted small">Keine Zeitbedingungen verfügbar</span>
                        : timeConditions.map(tc => {
                            const checked = (formData.auto_disable_tc_ids || []).includes(tc.id)
                            return (
                              <Form.Check
                                key={tc.id}
                                type="checkbox"
                                id={`autodisable-tc-${tc.id}`}
                                label={tc.name}
                                checked={checked}
                                onChange={(e) => {
                                  const cur = formData.auto_disable_tc_ids || []
                                  const next = e.target.checked
                                    ? [...cur, tc.id]
                                    : cur.filter(id => id !== tc.id)
                                  setFormData({...formData, auto_disable_tc_ids: next})
                                }}
                              />
                            )
                          })
                      }
                    </div>
                    <Form.Text className="text-muted">
                      Sobald eine der hier markierten Zeitbedingungen zutrifft, wird die Nachtschaltung automatisch deaktiviert (z.B. Geschäftszeiten = aktiv → Nachtschaltung aus).
                      Die Zeitbedingung hat Vorrang. Nicht markierte Zeitbedingungen haben keinen Einfluss — das schützt vor versehentlichem Deaktivieren durch Spezial-TCs.
                    </Form.Text>
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="link">
                <Accordion.Header>
                  <i className="bi bi-link-45deg me-2"></i>Verlinkung mit anderen Nachtschaltungen (optional)
                </Accordion.Header>
                <Accordion.Body>
                  <Form.Group>
                    <Form.Label>Diese Nachtschaltungen ausschalten, wenn diese hier eingeschaltet wird</Form.Label>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px' }}>
                      {entries.filter(e => e.id !== editingEntry?.id).length === 0
                        ? <span className="text-muted small">Keine anderen Nachtschaltungen verfügbar</span>
                        : entries.filter(e => e.id !== editingEntry?.id).map(other => {
                            const checked = (formData.linked_nm_ids || []).includes(other.id)
                            return (
                              <Form.Check
                                key={other.id}
                                type="checkbox"
                                id={`linknm-${other.id}`}
                                label={
                                  <>
                                    {other.name}
                                    {other.feature_code && <span className="text-muted small ms-2">({other.feature_code})</span>}
                                    {other.is_enabled && <Badge bg="success" className="ms-2">Aktiv</Badge>}
                                  </>
                                }
                                checked={checked}
                                onChange={(e) => {
                                  const cur = formData.linked_nm_ids || []
                                  const next = e.target.checked
                                    ? [...cur, other.id]
                                    : cur.filter(id => id !== other.id)
                                  setFormData({...formData, linked_nm_ids: next})
                                }}
                              />
                            )
                          })
                      }
                    </div>
                    <Form.Text className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Wenn diese Nachtschaltung eingeschaltet wird (manuell oder automatisch), werden alle hier markierten Nachtschaltungen automatisch ausgeschaltet — praktisch für sich gegenseitig ausschliessende Zustände (z.B. "Mittagspause" vs "Nacht").
                      Damit eine echte gegenseitige Verlinkung entsteht, muss die Markierung in beiden Richtungen gesetzt werden.
                    </Form.Text>
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingEntry ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Clone modal */}
      <Modal show={!!cloneSource} onHide={closeCloneModal}>
        <Modal.Header closeButton>
          <Modal.Title>Nachtschaltung klonen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Quelle: <strong>{cloneSource?.name}</strong>. Die Kopie übernimmt
            Ziel, Reichweite, Auto-Regeln und Verlinkungen, startet aber
            deaktiviert und erhält einen eigenen Feature-Code.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Feature-Code (optional)</Form.Label>
            <Form.Control
              type="text"
              value={cloneFeatureCode}
              onChange={(e) => setCloneFeatureCode(e.target.value)}
              placeholder="leer = automatisch vergeben"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCloneModal}>Abbrechen</Button>
          <Button variant="primary" onClick={handleClone} disabled={!cloneName.trim()}>
            Klonen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
